<script setup lang="ts" generic="T">import { toRef as _toRef, ref as _ref, computed as _computed } from 'vue';


import {StyleValue, watch, nextTick, useSlots, onBeforeUnmount, computed} from 'vue'
import {MaybeElementRef, useElementBounding, watchDebounced} from '@vueuse/core'
import InfinityScrollComponent from '@toolify/client/src/modules/ui/components/InfinityScrollComponent.vue'
import Sortable from 'sortablejs'
import ScrollbarComponent from '@toolify/client/src/modules/ui/components/ScrollbarComponent.vue'
import InputComponent from '@toolify/client/src/modules/forms/components/inputs/InputComponent.vue'
import InputWithIconComponent from '@toolify/client/src/modules/forms/components/InputWithIconComponent.vue'
import {useI18n} from 'vue-i18n'
import {MaybeElement} from '@floating-ui/vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import ChooseItemSkeletonComponent from '@toolify/client/src/modules/list/components/ChooseItemSkeletonComponent.vue'
import {CypressUniqueNameType} from '@toolify/client/cypress/types/CypressUniqueNameType'
import {PropertyStrategyThemeType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyThemeType'
import {useKeyboardShortcuts} from '../../keyboard/composables/useKeyboardShortcuts'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'
import {KeyboardModifierKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardModifierKeyTypes'
import {useSizeScale} from '@toolify/client/src/composables/useRem'
import {SizeTokenType} from '@toolify/client/src/modules/theme/enum/SizeTokenType'
import {SpaceTokenType} from '@toolify/client/src/modules/theme/enum/SpaceTokenType'
import {useResponsive} from '@toolify/client/src/composables/useResponsive'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useDropdownAvailableSpace} from '@toolify/client/src/modules/modals/composables/useDropdownAvailableSpace'
import {InputBackgroundType} from '@toolify/client/src/modules/forms/enum/InputBackgroundType'
import {InputType} from '@toolify/client/src/modules/forms/enum/InputType'
import {WidthTokenType} from '@toolify/client/src/modules/theme/enum/WidthTokenType'
import {easeOutCubic} from '@toolify/client/src/modules/animations/functions/easeOutCubic'

interface IItem {
  id?: string
}

interface IGroupItem {
  title: string
  items: T[]
}

interface Props {
  search: string
  shouldShowLoadMore?: boolean
  items: IGroupItem[]| T[]
  selectedIndexes: number[]
  hasLabels?: boolean
  allowDrag?: boolean
  showBottomPanel?: boolean
  isBottomPanelPrimaryAction?: boolean
  allowChoose?: boolean
  allowEdit?: boolean
  width?: number
  showTopPanel?: boolean
  fillAllPlace?: boolean
  isLoading?: boolean
  hideSearch?: boolean
  emptyText?: string
  maxListHeight?: number
  hideScrollGradients?: boolean
  clearFocusOnClick?: boolean
  containerPadding?: [number, number]
  separatorIndexes?: number[]
  preventScrollOnFocus?: boolean
  sortableItemCount?: number
}

interface Emits {
  (e: 'fetch', data: {
    skip: number,
    limit: number,
    shouldAppend: boolean,
    isSearchTriggered: boolean,
    hasLabels?: boolean,
  }): void
  (e: 'update:search', value: string): void
  (e: 'chooseItem', index: number): void
  (e: 'downPanelClick', value: string): void
  (e:'focusItem', index: number): void
  (e: 'drag', data: {
    oldIndex: number,
    newIndex: number,
  }): void
  (e: 'close'): void
  (e: 'tab'): void
  (e: 'shiftTab'): void
  (e: 'enter'): void
}

const {t} = useI18n()
const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');
const dropdownAvailableSpace = useDropdownAvailableSpace()
const props = withDefaults(defineProps<Props>(), {
  shouldShowLoadMore: false,
  allowDrag: false,
  showBottomPanel: false,
  isBottomPanelPrimaryAction: false,
  allowChoose: false,
  allowEdit: false,
  showTopPanel: false,
  width: undefined,
  fillAllPlace: false,
  isLoading: false,
  hideSearch: false,
  emptyText: null,
  maxListHeight: null,
  hideScrollGradients: false,
  clearFocusOnClick: false,
  selectIconColor: null,
  unselectIconColor: null,
  containerPadding: () => [0, 0],
  separatorIndexes: () => [],
  preventScrollOnFocus: false,
  sortableItemCount: null,
})
const emit = defineEmits<Emits>()

const {sizePx, spacePx, widthPx} = useSizeScale()
const {isSmallerThanLarge} = useResponsive()
let searchTmp = _ref('')
let focusedIndex = _ref<number>(null)
const scrollbarRef = _ref(null)
const itemHeight = computed(() => sizePx(
  isSmallerThanLarge.value ? SizeTokenType.Touch : SizeTokenType.Xl,
))
const labelHeight = computed(() => sizePx(SizeTokenType.Xs))
const labelPadding = computed(() => [spacePx(SpaceTokenType.Sm), spacePx(SpaceTokenType.Md)])
const lineHeight = 1
const lineMargin = computed(() => [spacePx(SpaceTokenType.Sm), spacePx(SpaceTokenType.SmPlus)])

const labelHeightValue = computed(() => `${labelHeight.value}px`)
const lineHeightValue = `${lineHeight}px`
const labelPaddingValue = computed(() => `${labelPadding.value[0]}px ${labelPadding.value[1]}px`)
const lineMarginValue = computed(() => `${lineMargin.value[0]}px ${lineMargin.value[1]}px`)

const fetchLimit = 30
const SKELETON_ITEM_COUNT = 5
const LOAD_MORE_SKELETON_ITEM_COUNT = 2

let scrollY = _ref(0)

const __$temp_2 = (useElementBounding((scrollbarRef) as unknown as MaybeElementRef<MaybeElement<null>>)),
  scrollContainerHeight = _toRef(__$temp_2, 'height');

watch((searchTmp), async (newVal, oldVal) => {
  if((props.items?.length === 0 && !props.showBottomPanel)) {
    focusedIndex.value = null
  } else {
    focusedIndex.value = 0
  }

  emit('update:search', newVal)

  if (scrollbarRef.value?.scrollToY && newVal !== oldVal) {
    scrollY.value = 0
    await nextTick()
    scrollbarRef.value?.scrollToY?.(0)
  }
})

watchDebounced((searchTmp), async () => {
  emit('fetch', {
    skip: 0,
    limit: fetchLimit,
    shouldAppend: false,
    isSearchTriggered: true,
  })
}, {
  debounce: 500,
})

const {canHover} = useResponsive()

const onItemClick = (index) => {
  focusedIndex.value = index
  if(!props.allowChoose) {
    return
  }
  if(Number(focusedIndex.value) > Number(maxIndex.value)) {
    return emit('chooseItem', null)
  }
  emit('chooseItem', index)

  if(props.clearFocusOnClick) {
    focusedIndex.value = null
  }

  if(canHover.value) {
    nextTick(() => {
      inputRef.value?.focus?.()
    })
  }
}

const inputRef = _ref<InstanceType<typeof InputComponent>|null>(null)

const maxIndex = _computed<number>(() => {
  if(props.hasLabels) {
    return props.items.reduce((acc, curr) => acc + (curr as IGroupItem).items.length, 0)
  }
  return props.items.length - 1
})

const itemsCount = _computed<number>(() => {
  if(props.hasLabels) {
    return props.items.reduce((acc, curr) => acc + (curr as IGroupItem).items.length, 0)
  }
  return props.items.length
})

const sortableSplitIndex = _computed<number>(() => {
  if(props.sortableItemCount === null) {
    return props.items.length
  }
  return Math.min(props.sortableItemCount, props.items.length)
})

const sortableItems = _computed<T[]>(() => {
  return (props.items as T[]).slice(0, sortableSplitIndex.value)
})

const extraItems = _computed<T[]>(() => {
  return (props.items as T[]).slice(sortableSplitIndex.value)
})

// The create row only becomes the primary action once the search matches nothing,
// otherwise it would compete with the highlighted item that Enter picks.
const isBottomPanelPrimary = computed(() => props.isBottomPanelPrimaryAction && itemsCount.value === 0)

useKeyboardShortcuts([
  [[KeyboardSpecialKeyTypes.Enter], () => {
    if (focusedIndex.value === null || focusedIndex.value < 0) {
      return
    }

    if (focusedIndex.value >= itemsCount.value) {
      onDownPanelClick()
      return
    }

    onItemClick(focusedIndex.value)
  }],
  [[KeyboardModifierKeyTypes.Shift, KeyboardSpecialKeyTypes.Tab], () => {
    emit('shiftTab')
  }],
  [[KeyboardSpecialKeyTypes.Tab], () => {
    emit('tab')
  }],
  [[KeyboardSpecialKeyTypes.Down], () => {
    if (focusedIndex.value === null) {
      focusedIndex.value = 0
    } else {
      focusedIndex.value++
    }

    checkIfFocusedIndexIsOutOfBounds()
    emit('focusItem', focusedIndex.value)
  }],
  [[KeyboardSpecialKeyTypes.Up], () => {
    focusedIndex.value--
    checkIfFocusedIndexIsOutOfBounds()
    emit('focusItem', focusedIndex.value)
  }],

  [[KeyboardSpecialKeyTypes.Escape], () => {
    emit('close')
  }],
])

//get index of group where is item
const currentGroupIndex = _computed(() => {
  if(focusedIndex.value === null || focusedIndex.value < 0) {
    return null
  }
  if(props.hasLabels) {
    return props.items.reduce((acc, curr, index) => {
      const current = curr as IGroupItem
      if(current.items.length + acc.sum > focusedIndex.value && focusedIndex.value >= acc.sum) {
        return {
          sum: acc.sum + current.items.length,
          result: index,
        }
      }
      return {
        sum: acc.sum + current.items.length,
        result: acc.result,
      }
    }, {
      sum: 0,
      result: null,
    }).result
  }
  return null
})

const checkIfFocusedIndexIsOutOfBounds = () => {
  if(focusedIndex.value < 0) {
    focusedIndex.value = 0
  }
  if(props.hasLabels) {
    const totalItems = props.items.reduce((acc, curr) => acc + (curr as IGroupItem).items.length, 0)
    if(focusedIndex.value > totalItems - 1) {
      focusedIndex.value = props.showBottomPanel ? totalItems : totalItems - 1
    }
    const currentIndexY = getItemOffsetY(focusedIndex.value)
    if(currentIndexY > scrollContainerHeight.value - itemHeight.value + scrollY.value) {
      scrollbarRef.value.scrollToY(currentIndexY - scrollContainerHeight.value + 1.3 * itemHeight.value)
    }
    if(currentIndexY < scrollY.value) {
      scrollbarRef.value.scrollToY(currentIndexY - 1.3 * itemHeight.value)
    }
  } else {
    if (focusedIndex.value > props.items.length - 1) {
      if(props.showBottomPanel) {
        focusedIndex.value = props.items.length
      } else {
        focusedIndex.value = props.items.length - 1
      }
    }
    if (focusedIndex.value * itemHeight.value > scrollContainerHeight.value - itemHeight.value + scrollY.value) {
      scrollbarRef.value.scrollToY(focusedIndex.value * itemHeight.value - scrollContainerHeight.value + 1.3 * itemHeight.value)
    }
    if (focusedIndex.value * itemHeight.value < scrollY.value) {
      scrollbarRef.value.scrollToY(focusedIndex.value * itemHeight.value)
    }
  }
}

const onScroll = ({y}) => {
  scrollY.value = y
}

const onLoadMore = async (isVisible: boolean) => {
  if(!isVisible) {
    return
  }
  emit('fetch', {
    skip: itemsCount.value,
    limit: fetchLimit,
    shouldAppend: true,
    isSearchTriggered: false,
  })
}

let sortable: Sortable = null

const destroySortable = () => {
  sortable?.destroy()
  sortable = null
}

onBeforeUnmount(destroySortable)

watch(() => props.isLoading, (newVal) => {
  sortable?.option('disabled', newVal)
})

const setItemsRef = (newItemsRef) => {
  const sortableElement = props.allowDrag ? newItemsRef : null
  if((sortable?.el ?? null) === sortableElement) {
    return
  }
  destroySortable()
  if(!sortableElement) {
    return
  }

  let scrollAnimationId = null
  const smoothScroll = (targetScroll: number, duration = 300) => {
    const startScroll = scrollY.value
    const startTime = performance.now()
    const change = targetScroll - startScroll

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      const easedProgress = easeOutCubic(progress)
      const currentScroll = startScroll + (change * easedProgress)

      scrollbarRef.value.scrollToY(currentScroll)

      if (progress < 1) {
        scrollAnimationId = requestAnimationFrame(animateScroll)
      }
    }

    if (scrollAnimationId) {
      cancelAnimationFrame(scrollAnimationId)
    }
    scrollAnimationId = requestAnimationFrame(animateScroll)
  }

  sortable = Sortable.create(sortableElement, {
    animation: 150,
    disabled: props.isLoading,
    group: {
      name: 'sortable-items',
    },
    handle: '.drag',
    delay: 300,
    delayOnTouchOnly: true,
    touchStartThreshold: 6,
    onMove(e) {
      const scrollContainer = scrollbarRef.value?.$el?.querySelector('.ps')
      if (!scrollContainer) return

      const containerRect = scrollContainer.getBoundingClientRect()
      // TODO check this is working
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const mouseY = e.originalEvent.clientY

      const scrollZone = itemHeight.value + 10
      const scrollStep = 60

      const distanceFromTop = mouseY - containerRect.top
      const distanceFromBottom = containerRect.bottom - mouseY

      if (distanceFromTop < scrollZone) {
        const scrollAmount = Math.max(-scrollStep, -scrollStep * (1 - distanceFromTop / scrollZone))
        const targetScroll = scrollY.value + scrollAmount
        smoothScroll(targetScroll, 200)
      } else if (distanceFromBottom < scrollZone) {
        const scrollAmount = Math.min(scrollStep, scrollStep * (1 - distanceFromBottom / scrollZone))
        const targetScroll = scrollY.value + scrollAmount
        smoothScroll(targetScroll, 200)
      }
    },
    onEnd() {
      if (scrollAnimationId) {
        cancelAnimationFrame(scrollAnimationId)
        scrollAnimationId = null
      }
    },
    async onUpdate(e) {
      emit('drag', {
        oldIndex: e.oldIndex,
        newIndex: e.newIndex,
      })
    },
  })
}

watch(() => props.search, (newVal) => {
  searchTmp.value = newVal
})
const slots = useSlots()

const containerStyle = _computed<StyleValue>(() => {
  const rows: string[] = []

  if (!props.hideSearch) {
    rows.push('auto')
  }

  if (slots['actions'] !== undefined) {
    rows.push('auto')
  }

  rows.push('1fr')

  return {
    gridTemplateRows: rows.join(' '),
    width: props.fillAllPlace ? '100%' : `${props.width ?? widthPx(WidthTokenType.List)}px`,
    minHeight: 0,
  }
})

const isInDropdown = _computed(() => dropdownAvailableSpace.value.availableHeight != null)

const onDownPanelClick = () => {
  if(!props.showBottomPanel) {
    return
  }
  emit('downPanelClick', searchTmp.value)
}

const showEmptyText = _computed(() => {
  return itemsCount.value === 0 && !props.shouldShowLoadMore && !props.isLoading
})
const inputIconNameType = _computed(() => {
  if (searchTmp.value) {
    return IconNameType.Times
  }
  return IconNameType.Search
})

const additionalContainerStyles = _computed<StyleValue>(() => {
  return {
    paddingTop: `${props.containerPadding[0]}px`,
    paddingBottom: `${props.containerPadding[1]}px`,
  }
})

const getGroupRealIndex = (groupIndex: number, itemIndex: number) => {
  return itemIndex + props.items.slice(0, groupIndex).reduce((acc, curr) => acc + (curr as IGroupItem).items.length, 0)
}

const getItemOffsetY = (index: number): number => {
  let y = props.containerPadding[0]
  let globalIndex = 0
  const groups = props.items as IGroupItem[]
  for (let g = 0; g < groups.length; g++) {
    y += labelHeight.value
    for (let i = 0; i < groups[g].items.length; i++) {
      if (globalIndex === index) return y
      y += itemHeight.value
      globalIndex++
    }
    if (g < groups.length - 1) {
      y += lineHeight + lineMargin.value[0] * 2
    }
  }
  return y
}

const onEnter = (event) => {
  if(focusedIndex.value === null || focusedIndex.value < 0 || focusedIndex.value > maxIndex.value) {
    event.stopPropagation()
    emit('enter')
  }
}

const focusItem = (index = 0) => {
  if (!itemsCount.value) return

  const clampedIndex = Math.max(0, Math.min(index, itemsCount.value - 1))
  focusedIndex.value = clampedIndex

  if (props.hasLabels) {
    scrollbarRef.value.scrollToY(getItemOffsetY(clampedIndex))
  } else {
    scrollbarRef.value.scrollToY(clampedIndex * itemHeight.value)
  }
}

defineExpose({
  focusItem,
})

const onSearchIconClick = () => {
  if (inputIconNameType.value === IconNameType.Times) {
    searchTmp.value = ''
  }
  nextTick(() => {
    inputRef.value?.focus?.()
  })
}

</script>

<template>
  <div
    class="choose-item-container"
    :class="{'is-in-dropdown': isInDropdown}"
    :style="containerStyle"
    data-dropdown-self-sized
  >
    <ScrollbarComponent v-if="props.showTopPanel" :gradientColor="getThemeVariable(ThemeVariableType.ContainerColor)">
      <div class="selected-items-container">
        <slot name="chosen"/>
      </div>
    </ScrollbarComponent>
    <div class="input-wrapper" v-if="!props.hideSearch">
      <InputWithIconComponent
        :iconNameType="inputIconNameType"
        @iconClick="onSearchIconClick"
        fullWidth
      >
        <InputComponent
          class="input"
          :type="InputType.Text"
          :theme="PropertyStrategyThemeType.Search"
          :background="InputBackgroundType.Normal"
          :focusOnMounted="canHover ? true : false"
          :preventScrollOnFocus="props.preventScrollOnFocus"
          :placeholder="props.allowEdit ? $t('general.searchOrCreateNew') : $t('general.search')"
          v-model="searchTmp"
          ref="inputRef"
          fullWidth
          :data-cy="CypressUniqueNameType.PropertySearchInput"
          @keydown.enter="onEnter"
        />
      </InputWithIconComponent>
    </div>
    <div class="actions-wrapper" v-if="$slots.actions">
      <slot name="actions"/>
    </div>
    <div
      class="infinity-scroll-wrapper"
      :style="props.maxListHeight ? {maxHeight: `${props.maxListHeight}px`} : null"
    >
      <InfinityScrollComponent
        :gradientColor="props.hideScrollGradients ? '' : getThemeVariable(ThemeVariableType.ContainerColor)"
        ref="scrollbarRef"
        @scroll="onScroll"
        :shouldShowLoadMore="props.shouldShowLoadMore"
        @loadMoreVisibilityChange="onLoadMore"
      >
        <template v-if="props.isLoading">
          <div class="skeleton-items-container" v-if="!props.shouldShowLoadMore" :style="additionalContainerStyles">
            <div
              v-for="skeletonIndex in SKELETON_ITEM_COUNT"
              :key="skeletonIndex"
              class="tw-border-b-2 tw-border-container lg:tw-border-none last:tw-border-none"
            >
              <slot name="skeletonItem" :index="skeletonIndex" :itemHeight="itemHeight">
                <ChooseItemSkeletonComponent :index="skeletonIndex" :itemHeight="itemHeight"/>
              </slot>
            </div>
          </div>
        </template>
        <template v-else-if="!props.items?.length">
          <div class="info-wrapper" v-if="showEmptyText">
            <span>{{props.emptyText ?? $t('general.noItems')}}</span>
          </div>
        </template>
        <template v-else-if="!hasLabels">
          <div
            class="all-items-container choose-items-region"
            :ref="(ref) => setItemsRef(ref)"
            :style="additionalContainerStyles"
          >
            <!-- math.random is needed e.g. when you create a new dropdown column and the newly created
              labels are not yet added to the database so they have no id then reordering the dropdowns correctly does not refresh the items because key is just an index -->
            <div
              v-for="(item, index) in sortableItems"
              :key="(item as IItem)?.id ? index + (item as IItem)?.id : index + Math.random()"
              :class="['item-container', 'tw-border-b-2 tw-border-container lg:tw-border-none last:tw-border-none', {'is-focused': index === focusedIndex}]"
              @click="onItemClick(index)"
            >
              <slot
                name="item"
                :item="item as T"
                :index="index"
                :isSelected="props.selectedIndexes.includes(index)"
                :itemHeight="itemHeight"
              />
              <div class="group-line" v-if="props.separatorIndexes?.includes(index)"/>
            </div>
          </div>
          <slot name="afterSortableItems" :itemHeight="itemHeight"/>
          <div
            class="extra-items-container choose-items-region tw-flex tw-flex-col tw-w-full tw-box-border"
            v-if="extraItems.length"
          >
            <div
              v-for="(item, offset) in extraItems"
              :key="(item as IItem)?.id ? offset + (item as IItem)?.id : offset + Math.random()"
              :class="['item-container', 'tw-border-b-2 tw-border-container lg:tw-border-none last:tw-border-none', {'is-focused': sortableSplitIndex + offset === focusedIndex}]"
              @click="onItemClick(sortableSplitIndex + offset)"
            >
              <slot
                name="item"
                :item="item as T"
                :index="sortableSplitIndex + offset"
                :isSelected="props.selectedIndexes.includes(sortableSplitIndex + offset)"
                :itemHeight="itemHeight"
              />
            </div>
          </div>
        </template>
        <div
          class="all-items-container"
          :data-cy="CypressUniqueNameType.PropertiesContainer"
          :ref="(ref) => setItemsRef(ref)"
          v-else
          :style="additionalContainerStyles"
        >
          <div
            v-for="(groupItem, groupIndex) in props.items as IGroupItem[]"
            :key="groupIndex"
          >
            <div class="label-wrapper dark-text">
              <slot name="label" :title="groupItem.title" :groupIndex="groupIndex">
                <span class="label-text">{{groupItem.title}}</span>
              </slot>
            </div>
            <div
              v-for="(item, index) in groupItem.items"
              :key="(item as IItem)?.id ? index + (item as IItem)?.id : index + Math.random()"
              :class="['item-container', 'tw-border-b-2 tw-border-container lg:tw-border-none last:tw-border-none', {'is-focused': getGroupRealIndex(groupIndex, index) === focusedIndex}]"
              @click="onItemClick(getGroupRealIndex(groupIndex, index))"
            >
              <slot
                name="item"
                :item="item as T"
                :index="getGroupRealIndex(groupIndex, index)"
                :isSelected="props.selectedIndexes.includes(getGroupRealIndex(groupIndex, index))"
                :itemHeight="itemHeight"
              />
            </div>
            <div class="group-line" v-if="groupIndex !== props.items.length - 1"/>
          </div>
        </div>
        <template #loader>
          <div class="skeleton-items-container" :style="props.isLoading || !itemsCount ? additionalContainerStyles : null">
            <div
              v-for="skeletonIndex in props.isLoading || !itemsCount ? SKELETON_ITEM_COUNT : LOAD_MORE_SKELETON_ITEM_COUNT"
              :key="skeletonIndex"
              class="tw-border-b-2 tw-border-container lg:tw-border-none last:tw-border-none"
            >
              <slot name="skeletonItem" :index="skeletonIndex" :itemHeight="itemHeight">
                <ChooseItemSkeletonComponent :index="skeletonIndex" :itemHeight="itemHeight"/>
              </slot>
            </div>
          </div>
        </template>
      </InfinityScrollComponent>
    </div>
    <div
      class="create-wrapper"
      :class="{
        'is-focused': focusedIndex!==null&&focusedIndex>=itemsCount&&searchTmp,
        'is-primary-action': isBottomPanelPrimary,
      }"
      v-if="props.showBottomPanel"
      @click="onDownPanelClick"
      @mouseenter="focusedIndex = Number(itemsCount)"
    >
      <slot name="bottom" :title="searchTmp" :isPrimaryAction="isBottomPanelPrimary"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;
@use '../../../styles/mixins' as mixins;
@use '@toolify/client/src/styles/light' as light;

.choose-item-container{
  box-sizing: border-box;
  display: grid;
  width: 100%;
  max-height: 100%;
  min-height: 0;
  justify-self: center;
  flex-direction: column;
  background: map-get(variables.$colors, container);
  // light: tlo tresci menu (przezroczyste). only-flat: nadpisania rodzicow przez :deep() dalej wygrywaja
  @include light.only-flat {
    background: map-get(variables.$colors, menu-content-surface);
  }
  overflow: hidden;

  &.is-in-dropdown {
    @media (min-width: 1024px) {
      @include mixins.cappedDropdownHeight();
    }
  }
}

.label-wrapper{
  display: flex;
  height: v-bind(labelHeightValue);
  padding: v-bind(labelPaddingValue);
  align-items: center;
}

.label-text{
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.group-line{
  height: v-bind(lineHeightValue);
  margin: v-bind(lineMarginValue);
  align-self: stretch;
  box-sizing: border-box;
  background: map-get(variables.$colors, container-high);
  display: none;
  @media (min-width: 1024px) {
    display: block;
  }

  // light: w menu container-high to kolor hovera - linia grup znikala; separator jak ContextMenuSeparator
  @include light.only-flat {
    background: map-get(variables.$colors, divider);
  }
}

.infinity-scroll-wrapper{
  height: 100%;
  width: 100%;
  min-height: 0;
  max-height: 100%;
  display: flex;
  overflow: hidden;
  padding: map-get(variables.$dropdownPadding, menuPosition) 0;
}

.item-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  @include mixins.menuRowInteraction;
}

.info-wrapper{
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: map-get(variables.$spacing, space-3);
  height: map-get(variables.$sizes, size-3xl);
  width: 100%;
  font-size: map-get(variables.$textSizes, text-xs);
  color: map-get(variables.$colors, content-dim);
}
.skeleton-items-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 map-get(variables.$dropdownPadding, menuPosition);
}

.selected-items-container{
  min-height: map-get(variables.$sizes, size-md);
  max-height: 110px;
  padding: map-get(variables.$spacing, space-1);
  gap: map-get(variables.$spacing, space-1);
  display: flex;
  flex-wrap: wrap;
}

.all-items-container{
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 0 map-get(variables.$dropdownPadding, menuPosition);
  overflow: auto;
  min-height: 0;
}

.extra-items-container{
  padding: 0 map-get(variables.$dropdownPadding, menuPosition);
}

.input-wrapper{
  display: flex;
  align-items: center;
  padding: map-get(variables.$dropdownPadding, menuPosition) map-get(variables.$dropdownPadding, menuPosition) 0 map-get(variables.$dropdownPadding, menuPosition);
  i{
    margin-right: 0.5rem;
    color: map-get(variables.$colors, content-dark);
    cursor: pointer;
  }
  @media (max-width: 1023px) {
    &:deep(.input){
      border-radius: map-get(variables.$radiuses, radius-full);
    }
  }
}

.create-wrapper{
  overflow: hidden;
  display: flex;
  gap: map-get(variables.$spacing, space-1);
  align-items: center;
  padding: map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-2);
  cursor: pointer;
  width: calc(100% - 2 * #{map-get(variables.$dropdownPadding, menuPosition)});
  box-sizing: border-box;
  transition: background 0.15s ease;
  margin: 0 map-get(variables.$dropdownPadding, menuPosition) map-get(variables.$dropdownPadding, menuPosition) map-get(variables.$dropdownPadding, menuPosition);
  border-radius: map-get(variables.$componentRadius, standard);
  
  &:hover{
    background: map-get(variables.$colors, container);
  }
  &.is-focused{
    background: map-get(variables.$colors, container-high);
  }
  &.is-focused:hover{
    background: map-get(variables.$colors, container-high);
    color: map-get(variables.$colors, primary-hover);
  }

  &.is-focused :deep(i),
  &.is-focused :deep(svg){
    color: inherit;
  }

  &.is-primary-action {
    background: map-get(variables.$colors, primary);
    color: map-get(variables.$colors, on-primary);

    &:hover,
    &.is-focused,
    &.is-focused:hover {
      background: map-get(variables.$colors, primary-hover);
      color: map-get(variables.$colors, on-primary-hover);
    }

    :deep(i),
    :deep(svg){
      color: inherit;
    }
  }
}
.dark-text{
  color: map-get(variables.$colors, content-dark);
}
</style>
